import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { Lock } from "lucide-react";
import { CONSULTATION_TYPES } from "../constants/consultation";

/**
 * Reusable appointment slot picker.
 * Props:
 *  - value: {date, hhmm, consultation_type, fees, is_walkin} (or null)
 *  - onChange(value)
 *  - defaultDate (YYYY-MM-DD)
 */
export default function AppointmentSlotPicker({ value, onChange, defaultDate }) {
  const [date, setDate] = useState(value?.date || defaultDate || new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState({ primary_slots: [], walkin_slots: [], walkin_unlocked: false });
  const [loading, setLoading] = useState(false);

  const emit = (patch) => {
    onChange?.({
      date,
      hhmm: value?.hhmm || "",
      consultation_type: value?.consultation_type || "General consultation",
      fees: value?.fees ?? 250,
      is_walkin: value?.is_walkin || false,
      ...patch,
    });
  };

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    api
      .get("/appointments/available-slots", { params: { date } })
      .then((r) => setSlots(r.data))
      .finally(() => setLoading(false));
  }, [date]);

  const pickSlot = (s) => {
    if (!s.available) return;
    emit({ hhmm: s.hhmm, is_walkin: s.is_walkin });
  };

  const renderSlot = (s) => {
    const active = value?.hhmm === s.hhmm;
    const disabled = !s.available;
    return (
      <button
        key={s.hhmm}
        type="button"
        disabled={disabled}
        onClick={() => pickSlot(s)}
        data-testid={`slot-${s.hhmm}`}
        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors border ${
          active
            ? "bg-primary text-primary-foreground border-primary"
            : disabled
            ? "bg-muted text-muted-foreground border-border cursor-not-allowed line-through"
            : "bg-white text-foreground border-border hover:border-primary/50"
        }`}
      >
        {s.label}
      </button>
    );
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="min-w-0">
          <Label>Date *</Label>
          <Input
            type="date"
            required
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              emit({ date: e.target.value, hhmm: "" });
            }}
            data-testid="appointment-date"
            className="w-full"
          />
        </div>
        <div className="min-w-0">
          <Label>Consultation type</Label>
          <Select
            value={value?.consultation_type || "General consultation"}
            onValueChange={(v) => emit({ consultation_type: v })}
          >
            <SelectTrigger data-testid="slot-consultation-type" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CONSULTATION_TYPES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Fees (₹)</Label>
        <Input
          type="number"
          min={0}
          value={value?.fees ?? 250}
          onChange={(e) => emit({ fees: parseInt(e.target.value) || 0 })}
          data-testid="appointment-fees"
        />
      </div>

      <div>
        <div className="flex items-baseline justify-between">
          <Label>Time slot *</Label>
          <span className="text-[11px] text-muted-foreground">5:30–6:30 PM</span>
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground py-3">Loading slots…</p>
        ) : (
          <div className="grid grid-cols-4 gap-2 mt-1">
            {(slots.primary_slots || []).map(renderSlot)}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-baseline justify-between">
          <Label className="flex items-center gap-1">
            Walk-in slots {!slots.walkin_unlocked && <Lock className="inline size-3" />}
          </Label>
          <span className="text-[11px] text-muted-foreground">7:00–8:00 PM · available when primary is full</span>
        </div>
        {slots.walkin_unlocked ? (
          <div className="grid grid-cols-4 gap-2 mt-1">
            {(slots.walkin_slots || []).map(renderSlot)}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground py-2 italic">
            Locked — primary slots still available.
          </p>
        )}
      </div>
    </div>
  );
}

export const slotToScheduledAt = (val) => {
  if (!val?.date || !val?.hhmm) return null;
  // Build IST ISO
  return `${val.date}T${val.hhmm}:00+05:30`;
};
